<script lang="ts" setup>
import { BaseObjDao } from '@/dao/BaseObjDao';
import { Race } from '@/objects/generated/classes/Race';
import { useEventStore, useGuiStore } from '@/stores';
import { ContentComp, DetailComp } from '@/components';
import { useTaskLoader } from '@/composeables/loaders';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import { ref, computed, watch } from 'vue';
import type { FieldAlertMessage } from '@/objects';
import { useBaseObjectStatus } from '@/composeables/baseobjstatus/baseObjectStatus';
import { TabDefinition } from '@/objects/Tabs';
import RaceTrackingView from '@/components/views/Races/RaceTrackingView.vue';
import { featureIcons } from '@/components/Menu/featureicons';
import { customGuiGroups } from './RacesUtil';
import { getRouterStoreRef } from '@/stores/routerStore';
import RaceVirtualView from './RaceVirtualView.vue';

const props = defineProps<{
    id: number;
}>();

const guiStore = useGuiStore();
const eventStore = useEventStore();

// Handle to the detail component
const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

// Set up the model with a default value
const race = ref<Race | null>(null);

// We need to watch the status of the object
const status = useBaseObjectStatus<Race>(race, () => race.value?.Name);

const additionalTabs: TabDefinition[] = [];
additionalTabs.push(new TabDefinition('virtual', 'race.virtual', 'fa-vr-cardboard'));
additionalTabs.push(new TabDefinition('tracking', 'contestant.tracking', featureIcons.TRACKING));

/**
 * We load the data from the backend
 * This is done in the exact same way in all of these views so I think we can find a more generic way to handle this
 */
const { AddLoader, LoadState } = useTaskLoader();

AddLoader(async () => {
    race.value = await BaseObjDao.LoadOrCreateSingle(Race, props.id, eventStore.eventID);
}, 'race.loading_race');

defineExpose<DetailViewExpose<Race>>({
    val: status.Value,
    title: status.Title,
    changed: status.Changed,
    messages: computed(() => (detailComp.value !== null ? detailComp.value!.messages : [])),
});

// Helper property to know if we need to render the Tracking component ( it is a bit heavy to load so this is a lazy load)
const isTrackingViewed = ref<boolean>(false);

const { CurrentRoute } = getRouterStoreRef();
watch(
    CurrentRoute,
    () => {
        if (!isTrackingViewed.value) {
            isTrackingViewed.value = CurrentRoute.value?.params?.subtab === 'tracking';
        }
    },
    { immediate: true },
);
</script>

<template>
    <ContentComp
        :load-state="LoadState"
        :fill-content="true"
    >
        <DetailComp
            :gui="guiStore.Race"
            v-model="race"
            :additional-tabs="additionalTabs"
            ref="detailComp"
            :custom-gui-groups="customGuiGroups"
            v-if="race"
        >
            <template #tab-tracking>
                <RaceTrackingView
                    v-model="race"
                    v-if="isTrackingViewed"
                />
            </template>
            <template #tab-virtual>
                <RaceVirtualView v-model="race" />
            </template>
        </DetailComp>
    </ContentComp>
</template>
